import { useState, useRef } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadImage, resolveImg, formatApiError } from "@/lib/api";

function Thumb({ url, onRemove }) {
  return (
    <div className="relative h-20 w-28 overflow-hidden border border-ptf-primary/15 group">
      <img src={resolveImg(url)} alt="" className="h-full w-full object-cover" />
      <button
        type="button"
        onClick={onRemove}
        data-testid="remove-image"
        className="absolute top-1 right-1 grid place-items-center h-6 w-6 bg-ptf-primary/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// Single or multiple image uploader for the admin.
// - single: value = string url, onChange(url)
// - multiple: value = array of urls, onChange(array)
export default function ImageUploader({ value, onChange, multiple = false, label = "Images" }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const urls = multiple ? (Array.isArray(value) ? value : []) : value ? [value] : [];

  const handleFiles = async (files) => {
    const list = multiple ? Array.from(files) : Array.from(files).slice(0, 1);
    if (list.length === 0) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of list) uploaded.push(await uploadImage(f));
      onChange(multiple ? [...urls, ...uploaded] : uploaded[0]);
      toast.success(multiple ? "Images uploaded" : "Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (i) => onChange(multiple ? urls.filter((_, idx) => idx !== i) : "");

  return (
    <div>
      <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ptf-text/60">{label}</span>
      <div className="mt-1 flex flex-wrap gap-3">
        {urls.map((u, i) => (
          <Thumb key={u} url={u} onRemove={() => removeAt(i)} />
        ))}
        {(multiple || urls.length === 0) && (
          <button
            type="button"
            data-testid="upload-image-btn"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="h-20 w-28 grid place-items-center border border-dashed border-ptf-primary/30 text-ptf-secondary hover:bg-ptf-primary/5 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple={multiple}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}
